"use client";

import { Button } from "@workspace/ui/components/button";
import Image from "next/image";
import Link from "next/link";
import { useEffect, useState } from "react";
import { useConfig } from "@/components/providers/config";
import { normalizeUrl } from "@/lib/utils";
import { NavigationLinks } from "./navigation-links";
import { NavigationSlideover } from "./navigation-slideover";

export function Header() {
  const { config } = useConfig();
  const headerBackground = false;
  const [scrollY, setScrollY] = useState(0);

  // Extract call to action link from config
  // Check for 'link' first (new Config type), then fallback to 'call_to_action_link' for backwards compatibility
  const ctaLink =
    (config as Record<string, unknown>)?.link ??
    (config as Record<string, unknown>)?.call_to_action_link ??
    null;
  const ctaHref: string =
    (ctaLink as { cached_url?: string; url?: string })?.cached_url ??
    (ctaLink as { cached_url?: string; url?: string })?.url ??
    "/contact";
  const ctaLabel: string =
    (ctaLink as { title?: string })?.title ??
    (typeof (config as Record<string, unknown>)?.call_to_action_label ===
    "string"
      ? ((config as Record<string, unknown>).call_to_action_label as string)
      : undefined) ??
    "Schedule A Meeting";

  useEffect(() => {
    const handleScroll = () => {
      setScrollY(window.scrollY);
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll(); // Check initial scroll position
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Calculate opacity: 0 at scrollY = 0, reaches 0.75 at scrollY = 96px
  const opacity = headerBackground
    ? 0.9
    : Math.min(0.9, Math.max(0, (scrollY / 96) * 0.9));

  const hasScrolled = scrollY > 0;

  return (
    <header
      className={`sticky h-24 top-0 z-50 flex flex-col justify-center backdrop-blur-md ${
        hasScrolled ? "shadow-2xl" : ""
      }`}
      style={{
        backgroundColor: `color-mix(in oklch, var(--background) ${opacity * 100}%, transparent)`,
        transition: "background-color 0.3s ease-out, box-shadow 0.3s ease-out",
      }}
    >
      <div className="p-4 sm:p-6 w-full">
        <div className="flex items-center justify-between relative gap-8">
          <div className="shrink-0 text-2xl font-extrabold">
            <Link href="/">
              <Image
                src="/images/logo.png"
                alt="The Learning Lab"
                width={150}
                height={150}
                className="h-16 w-auto hover:scale-102 transition-transform duration-300"
              />
            </Link>
          </div>
          <div className="2xl:absolute mr-auto 2xl:mr-none hidden lg:block 2xl:left-1/2 2xl:-translate-x-1/2">
            <NavigationLinks />
          </div>
          <div className="flex items-center gap-2">
            <NavigationSlideover />
            <Button size="lg" className="hidden lg:inline-flex" asChild>
              <Link
                href={normalizeUrl(ctaHref)}
                target="_blank"
                rel="noopener noreferrer"
              >
                {ctaLabel}
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </header>
  );
}
